import { HomeIcon, HeartIcon, CloudIcon, UserGroupIcon, MapIcon, StarIcon, ClockIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import "dayjs/locale/fr";

import Header from "common/components/Header/Header";
import useFetch from "hooks/useFetch";
import type { StatsData, ZoneWeatherStat } from "types/stats";

dayjs.locale("fr");

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string | number;
  Icon: React.ElementType;
  color: string;
  sub?: string;
}

function KpiCard({ label, value, Icon, color, sub }: KpiCardProps) {
  return (
    <div className="white-box flex items-center gap-4 p-4">
      <div className={`rounded-xl p-3 ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-800">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Weather Donut ───────────────────────────────────────────────────────────

function WeatherChart({ weather }: { weather: StatsData["weather"] }) {
  const options: ApexOptions = {
    chart: { type: "donut", animations: { enabled: true } },
    labels: ["Reçus", "Non reçus"],
    colors: ["#68A78E", "#e2e8f0"],
    legend: { position: "bottom", fontFamily: "DM Sans, sans-serif" },
    dataLabels: { style: { fontFamily: "DM Sans, sans-serif" } },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Couverture",
              fontFamily: "DM Sans, sans-serif",
              color: "#475569",
              formatter: () => `${weather.coveragePercent}%`,
            },
          },
        },
      },
    },
    tooltip: { style: { fontFamily: "DM Sans, sans-serif" } },
  };

  return (
    <div className="white-box p-4">
      <div className="flex items-center gap-2 mb-3">
        <CloudIcon className="w-5 h-5 text-primary" />
        <span className="font-semibold text-slate-700">Fichiers météo</span>
        <span className="ml-auto text-xs text-slate-400">{weather.total} zones</span>
      </div>
      <ReactApexChart
        options={options}
        series={[weather.withWeather, weather.withoutWeather]}
        type="donut"
        height={220}
      />
    </div>
  );
}

// ─── Reviews Bar ─────────────────────────────────────────────────────────────

function ReviewsChart({ reviews }: { reviews: StatsData["reviews"] }) {
  const seriesWeather = [1, 2, 3, 4, 5].map((g) => reviews.weather.distribution[g] ?? 0);
  const seriesAdvice = [1, 2, 3, 4, 5].map((g) => reviews.advice.distribution[g] ?? 0);
  const maxVal = Math.max(...seriesWeather, ...seriesAdvice);

  const options: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
    colors: ["#68A78E", "#f59e0b"],
    xaxis: {
      categories: ["★1", "★2", "★3", "★4", "★5"],
      labels: { style: { fontFamily: "DM Sans, sans-serif", colors: "#64748b" } },
    },
    yaxis: {
      labels: { style: { fontFamily: "DM Sans, sans-serif", colors: "#64748b" } },
      tickAmount: maxVal > 0 ? undefined : 1,
    },
    legend: { position: "bottom", fontFamily: "DM Sans, sans-serif" },
    dataLabels: { enabled: false },
    grid: { borderColor: "#f1f5f9" },
    tooltip: { style: { fontFamily: "DM Sans, sans-serif" } },
  };

  return (
    <div className="white-box p-4">
      <div className="flex items-center gap-2 mb-1">
        <StarIcon className="w-5 h-5 text-primary" />
        <span className="font-semibold text-slate-700">Satisfaction</span>
        {reviews.total > 0 && (
          <span className="ml-auto text-xs text-slate-400">({reviews.total} avis)</span>
        )}
      </div>
      {reviews.total > 0 && (
        <div className="flex gap-4 text-xs mb-1">
          <span className="text-emerald-600">Météo : <strong>{reviews.weather.average}/5</strong></span>
          <span className="text-amber-500">Conseils : <strong>{reviews.advice.average}/5</strong></span>
        </div>
      )}
      {reviews.total === 0 ? (
        <p className="text-center text-slate-400 text-sm py-10">Aucun avis pour l&apos;instant</p>
      ) : (
        <ReactApexChart
          options={options}
          series={[
            { name: "Météo", data: seriesWeather },
            { name: "Conseils", data: seriesAdvice },
          ]}
          type="bar"
          height={220}
        />
      )}
    </div>
  );
}

// ─── Zone Weather Table ───────────────────────────────────────────────────────

const PAGE_SIZE = 25;

function StatusIcon({ ok, label }: { ok: boolean; label?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${ok ? "text-emerald-600" : "text-red-500"}`}>
      {ok ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
      {label}
    </span>
  );
}

function ZoneWeatherTable({ zones }: { zones: ZoneWeatherStat[] }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const filtered = zones
    .filter((z) => !search || z.name?.toLowerCase().includes(search.toLowerCase()) || String(z.zoneId).includes(search))
    .sort((a, b) => a.zoneId - b.zoneId);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const page_ = Math.min(page, Math.max(0, totalPages - 1));
  const slice = filtered.slice(page_ * PAGE_SIZE, (page_ + 1) * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(0); };

  return (
    <div className="white-box mt-3 p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CloudIcon className="w-5 h-5 text-primary flex-shrink-0" />
        <span className="font-semibold text-slate-700">Diagnostic fichiers météo par zone</span>
        <span className="text-xs text-slate-400">{filtered.length} / {zones.length} zones</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Filtrer…"
              className="pl-7 pr-3 py-1 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary bg-white"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-slate-100 text-xs text-slate-400 uppercase tracking-wide">
              <th className="pb-2 pr-4 font-medium">Zone</th>
              <th className="pb-2 pr-4 font-medium text-center">Historique</th>
              <th className="pb-2 pr-4 font-medium text-center">Forecast</th>
              <th className="pb-2 pr-4 font-medium text-center">10 jours</th>
              <th className="pb-2 font-medium text-center">Historique complet</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((z) => {
              const active = z.enabled !== false;
              return (
                <tr key={z.zoneId} className={`border-b border-slate-50 last:border-0 ${!active ? "opacity-50" : ""}`}>
                  <td className="py-2 pr-4">
                    <span className="font-medium text-slate-700">{z.name}</span>
                    <span className="ml-2 text-xs text-slate-400">#{z.zoneId}</span>
                    {!active && (
                      <span className="ml-2 text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">désactivée</span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-center">
                    <StatusIcon ok={z.hasHistorical} />
                  </td>
                  <td className="py-2 pr-4 text-center">
                    <StatusIcon ok={z.hasForecast} />
                  </td>
                  <td className="py-2 pr-4 text-center">
                    {z.hasForecast ? (
                      <StatusIcon ok={z.forecast10Days} label={z.forecastDays != null ? `${z.forecastDays}j` : undefined} />
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="py-2 text-center">
                    {z.hasHistorical ? (
                      <StatusIcon ok={z.historicalComplete} label={`${z.historicalDays ?? 0}/${z.expectedHistoricalDays}j`} />
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {slice.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-slate-400 text-sm">Aucune zone trouvée</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>{page_ * PAGE_SIZE + 1}–{Math.min((page_ + 1) * PAGE_SIZE, filtered.length)} / {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page_ === 0}
              className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span>{page_ + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page_ === totalPages - 1}
              className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Home ─────────────────────────────────────────────────────────────────────

function Home() {
  const { t } = useTranslation();
  const { data: stats, isFetching } = useFetch<StatsData>("app/stats");

  const lastUpdateLabel = stats?.lastUpdate
    ? dayjs(stats.lastUpdate).format("DD MMM YYYY")
    : "—";

  return (
    <div className="py-6 relative">
      <div
        className="h-[300px] lg:h-[200px] w-full absolute top-0 left-0 flex bg-cover bg-center"
        style={{ backgroundImage: "url(/images/header-background.png)" }}
      />
      <div className="mx-auto text-sm max-w-full max-main-box px-4 mb-4 relative">
        <Header
          title={t("titlePages.board")}
          subtitle={t("titleLegends.board")}
          Icon={HomeIcon}
        />

        {isFetching && !stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="white-box h-24 animate-pulse bg-slate-100" />
            ))}
          </div>
        ) : stats ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              <KpiCard
                label="Santé"
                value={stats.health === "up" ? "En ligne" : "Hors ligne"}
                Icon={stats.health === "up" ? CheckCircleIcon : XCircleIcon}
                color={stats.health === "up" ? "bg-emerald-500" : "bg-red-500"}
              />
              <KpiCard
                label="Dernière MAJ"
                value={lastUpdateLabel}
                Icon={ClockIcon}
                color="bg-primary"
              />
              <KpiCard
                label="Utilisateurs"
                value={stats.usersCount}
                Icon={UserGroupIcon}
                color="bg-violet-500"
              />
              <KpiCard
                label="Parcelles"
                value={stats.plotsCount}
                Icon={MapIcon}
                color="bg-amber-500"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              <WeatherChart weather={stats.weather} />
              <ReviewsChart reviews={stats.reviews} />
            </div>

            {/* Zone weather diagnostics */}
            {stats.zoneWeatherStats && stats.zoneWeatherStats.length > 0 && (
              <ZoneWeatherTable zones={stats.zoneWeatherStats} />
            )}

            {/* Health detail */}
            <div className="white-box mt-3 p-4 flex items-center gap-3">
              <HeartIcon className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex items-center gap-2">
                {stats.health === "up" ? (
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircleIcon className="w-4 h-4 text-red-500" />
                )}
                <span className="text-slate-700 text-sm">
                  API{" "}
                  <strong>{stats.health === "up" ? "opérationnelle" : "indisponible"}</strong>
                  {" · "}
                  {stats.weather.withWeather}/{stats.weather.total} zones avec données météo
                  {" · "}
                  {stats.reviews.total} avis · météo{" "}
                  <strong>{stats.reviews.weather.average > 0 ? `${stats.reviews.weather.average}/5` : "n/a"}</strong>
                  {" · "}conseils{" "}
                  <strong>{stats.reviews.advice.average > 0 ? `${stats.reviews.advice.average}/5` : "n/a"}</strong>
                </span>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default Home;
